import { api } from '../config/api';

export const settingsService = {
  // Normalizar logo: asegurar data URL para base64
  normalizeLogo: (value) => {
    if (!value) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    // Evitar tratar JSON como imagen
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return '';
    }
    if (trimmed.startsWith('data:') || trimmed.startsWith('http')) {
      return trimmed;
    }
    // Validar base64 simple antes de armar data URL
    const isBase64 = /^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 50;
    if (!isBase64) {
      return '';
    }
    if (trimmed.startsWith('/9j/')) {
      return `data:image/jpeg;base64,${trimmed}`;
    }
    if (trimmed.startsWith('iVBOR')) {
      return `data:image/png;base64,${trimmed}`;
    }
    if (trimmed.startsWith('R0lGOD')) {
      return `data:image/gif;base64,${trimmed}`;
    }
    return `data:image/png;base64,${trimmed}`;
  },
  // Obtener una configuración por su clave
  getSetting: async (key) => {
    const response = await api.get(`/settings/?key=${encodeURIComponent(key)}`);
    if (Array.isArray(response)) {
      return response[0] || null;
    }
    if (Array.isArray(response?.data)) {
      return response.data[0] || null;
    }
    return null;
  },

  // Obtener el valor de una configuración
  getSettingValue: async (key) => {
    const response = await api.get(`/settings/?key=${encodeURIComponent(key)}`);
    const results = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : [];
    if (results.length === 0) {
      return null;
    }
    return results[0]?.value ?? null;
  },

  // Crear o actualizar una configuración
  createOrUpdateSetting: async (key, value, description = null) => {
    const stringValue = value === 0 || value === '0' ? '0' : String(value ?? '').trim();
    if (!stringValue) {
      // No enviar value vacío si el serializer no lo acepta
      return null;
    }
    const existing = await settingsService.getSetting(key);
    // Usar la clave del setting (key) directamente, no el ID numérico
    // El ViewSet usa lookup_field='key', así que la URL debe ser /settings/{key}/
    if (existing) {
      // Siempre usar el parámetro 'key' que se pasa a la función, no existing.key
      // porque existing.key podría ser el ID numérico en algunos casos
      return await api.put(`/settings/${encodeURIComponent(key)}/`, {
        key,
        value: stringValue,
        description
      });
    }
    return await api.post('/settings/', {
      key,
      value: stringValue,
      description
    });
  },

  // Obtener tipo de cambio
  getExchangeRate: async (currency = 'USD') => {
    const response = await api.get(`/settings/exchange-rate/${currency}`);
    return response.rate || 0;
  },

  // Establecer tipo de cambio
  setExchangeRate: async (currency, rate) => {
    return await api.put(`/settings/exchange-rate/${currency}?rate=${rate}`, null);
  },

  // Obtener objetivos trimestrales (usa endpoint especial del backend)
  getQuarterlyGoals: async () => {
    try {
      // Usar el endpoint especial /settings/quarterly-goals que devuelve el objeto directamente
      const response = await api.get('/settings/quarterly-goals');
      if (response && typeof response === 'object') {
        return response;
      }
      return { q1: 0, q2: 0, q3: 0, q4: 0 };
    } catch (error) {
      console.error('Error cargando objetivos trimestrales:', error);
      return { q1: 0, q2: 0, q3: 0, q4: 0 };
    }
  },

  // Guardar objetivos trimestrales (usa endpoint especial del backend)
  setQuarterlyGoals: async (goals) => {
    try {
      // Usar el endpoint especial /settings/quarterly-goals que espera el objeto directamente
      // No usar createOrUpdateSetting porque ese endpoint espera un formato diferente
      const payload = goals || { q1: 0, q2: 0, q3: 0, q4: 0 };
      return await api.put('/settings/quarterly-goals', payload);
    } catch (error) {
      console.error('Error guardando objetivos trimestrales:', error);
      throw error;
    }
  },

  // Obtener datos de empresa para cotizaciones
  getCompanyData: async () => {
    try {
      const name = await settingsService.getSettingValue('company_name') || '';
      const data = await settingsService.getSettingValue('company_data') || '';
      const rawLogo = await settingsService.getSettingValue('company_logo') || '';
      const logo = settingsService.normalizeLogo(rawLogo);
      return { name, data, logo };
    } catch (error) {
      console.error('Error cargando datos de empresa:', error);
      return { name: '', data: '', logo: '' };
    }
  },

  // Guardar datos de empresa para cotizaciones
  setCompanyData: async (companyData) => {
    try {
      const promises = [
        settingsService.createOrUpdateSetting('company_name', companyData.name || '', 'Nombre de la empresa para cotizaciones'),
        settingsService.createOrUpdateSetting('company_data', companyData.data || '', 'Datos de la empresa para cotizaciones'),
        settingsService.createOrUpdateSetting('company_logo', companyData.logo || '', 'Logo de la empresa para cotizaciones (base64 o URL)')
      ];
      await Promise.all(promises);
      return { success: true };
    } catch (error) {
      console.error('Error guardando datos de empresa:', error);
      throw error;
    }
  },

  // Subir logo de empresa (convierte a base64)
  uploadCompanyLogo: async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result;
          await settingsService.createOrUpdateSetting('company_logo', base64String, 'Logo de la empresa para cotizaciones');
          resolve(base64String);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  // Obtener timeout de deslogueo automático (en minutos, 0 = nunca se desloguea)
  getLogoutTimeout: async () => {
    try {
      const value = await settingsService.getSettingValue('logout_timeout_minutes');
      const timeout = parseInt(value) || 0;
      return timeout;
    } catch (error) {
      // Si el setting no existe (404), es normal - usar valor por defecto
      // Solo loguear si no es un 404 (error real)
      if (error.message && !error.message.includes('no encontrado') && !error.message.includes('404')) {
        console.error('Error obteniendo timeout de deslogueo:', error);
      }
      return 0; // Por defecto, nunca se desloguea
    }
  },

  // Establecer timeout de deslogueo automático (en minutos, 0 = nunca se desloguea)
  setLogoutTimeout: async (minutes) => {
    try {
      const timeout = parseInt(minutes) || 0;
      await settingsService.createOrUpdateSetting(
        'logout_timeout_minutes',
        String(timeout),
        'Timeout de deslogueo automático en minutos (0 = nunca se desloguea)'
      );
      return timeout;
    } catch (error) {
      console.error('Error estableciendo timeout de deslogueo:', error);
      throw error;
    }
  }
};

